import { col, type Shape } from './base.ts';

export const NETWORK_RELATION: Shape = [col('networkName', 'unitless', 'Reference to the network model entity.')];

/**
 * Separator operating + maximum limit conditions on `NetworkInputs_Agent`
 * The Agent source columns are already bare canonical names (no
 * prefix), so each `suffix === canonical` and the reshape is pure projection
 * with no rename (`prefix:''`).
 */
export const SEPARATOR_CONDITIONS: Shape = [
  col('separatorOilRate', 'liquidRate', 'Separator oil rate operating condition.'),
  col('separatorGasRate', 'gasRateModel', 'Separator gas rate operating condition.'),
  col('separatorWaterRate', 'liquidRate', 'Separator water rate operating condition.'),
  col('separatorPressure', 'pressureAbsolute', 'Separator operating pressure.'),
  col('separatorTemperature', 'temperature', 'Separator operating temperature.'),
  col('separatorMaxOilRate', 'liquidRate', 'Maximum separator oil rate.'),
  col('separatorMaxGasRate', 'gasRateModel', 'Maximum separator gas rate.'),
  col('separatorMaxWaterRate', 'liquidRate', 'Maximum separator water rate.'),
];

/**
 * Network joint (commingling node) operating constraints on
 * `NetworkInputs_Agent`: max phase/liquid rates, the pressure window,
 * and contaminant (CO2/N2/H2S) ceilings. Bare canonical source columns —
 * `suffix === canonical`, `prefix:''`.
 */
export const JOINT_CONSTRAINTS: Shape = [
  col('jointMaxOilRate', 'liquidRate', 'Maximum joint oil rate.'),
  col('jointMaxGasRate', 'gasRateModel', 'Maximum joint gas rate.'),
  col('jointMaxLiquidRate', 'liquidRate', 'Maximum joint liquid rate.'),
  col('jointMinPressure', 'pressureAbsolute', 'Minimum joint pressure.'),
  col('jointMaxPressure', 'pressureAbsolute', 'Maximum joint pressure.'),
  col('jointMaxCO2', 'fraction', 'Maximum joint CO2 mol fraction.'),
  col('jointMaxN2', 'fraction', 'Maximum joint N2 mol fraction.'),
  col('jointMaxH2S', 'fraction', 'Maximum joint H2S mol fraction.'),
];

/**
 * Flowline operating constraints on `NetworkInputs_Agent`: the erosional
 * mixture-velocity and C-factor limits plus the line-pressure ceiling. Bare
 * canonical source columns — `suffix === canonical`, `prefix:''`.
 */
export const FLOWLINE_CONSTRAINTS: Shape = [
  col('flowlineMaxMixtureVelocity', 'velocity', 'Maximum flowline mixture velocity (erosional limit).'),
  col('flowlineMaxCFactor', 'unitless', 'Maximum flowline erosional C-factor.'),
  col('flowlineMaxLinePressure', 'pressureAbsolute', 'Maximum flowline line pressure.'),
];

/**
 * Bare manifold conditions and well/pump operating limits on
 * `NetworkInputs_Agent` that fall outside the separator/joint/flowline
 * groups: manifold pressure/temperature, the well GOR ceiling, the surface-pump
 * liquid-rate ceiling, and the planned facility downtime assumption. Bare
 * canonical source columns — `suffix === canonical`, `prefix:''`.
 */
export const NETWORK_LIMITS: Shape = [
  col('manifoldPress', 'pressureAbsolute', 'Manifold operating pressure.'),
  col('manifoldTemp', 'temperature', 'Manifold operating temperature.'),
  col('wellMaxGOR', 'gasOilRatio', 'Maximum well gas-oil ratio.'),
  col('surfacePumpMaxLiquidRate', 'liquidRate', 'Maximum surface-pump liquid rate.'),
  col('plannedFacilityDowntime', 'unitless', 'Planned facility downtime assumption for the run.'),
];

/**
 * Network economic inputs on `NetworkInputs_Agent`: commodity prices,
 * per-phase processing/disposal/lift costs, and the gas barrel-of-oil-equivalent
 * conversion. Bare canonical source columns — `suffix === canonical`,
 * `prefix:''`.
 */
export const NETWORK_ECONOMICS: Shape = [
  col('oilPrice', 'pricePerBarrel', 'Oil price input to the network economics.'),
  col('gasPrice', 'pricePerBarrel', 'Gas price input to the network economics.'),
  col('oilProcessingCost', 'pricePerBarrel', 'Per-unit oil processing cost.'),
  col('gasProcessingCost', 'pricePerBarrel', 'Per-unit gas processing cost.'),
  col('liftCost', 'pricePerBarrel', 'Per-unit artificial-lift cost.'),
  col('waterDisposalCost', 'pricePerBarrel', 'Per-unit water disposal cost.'),
  col('gasBarrelOfOilEquivalent', 'unitless', 'Gas barrel-of-oil-equivalent conversion factor.'),
];

/**
 * Network capacity inputs on `NetworkInputs_Agent`: the per-phase
 * processing-rate capacities plus the available gas-lift rate and power
 * headroom. Bare canonical source columns — `suffix === canonical`, `prefix:''`.
 */
export const NETWORK_CAPACITY: Shape = [
  col('capacityGasRate', 'gasRate', 'Network gas-rate processing capacity.'),
  col('capacityOilRate', 'liquidRate', 'Network oil-rate processing capacity.'),
  col('capacityWaterRate', 'liquidRate', 'Network water-rate processing capacity.'),
  col('availableGLRate', 'gasRate', 'Available gas-lift injection rate.'),
  col('availablePower', 'power', 'Available electrical power headroom.'),
];
/**
 * The per-grain entity relation carried by each `DailyRollup{Grain}_Agent`
 * reshape view: the direct-relation to the grain's model entity. Kept
 * verbatim as the grain ref (the Agent source already names it `assetEntity`,
 * `companyEntity`, …), so the agent can scope by / join the grain entity.
 * Authored with `suffix === canonical` (passthrough, like
 * `IDENTITY`/`NETWORK_RELATION`) — the Agent source carries no prefix, so
 * `prefix:''` yields `source === canonical` and no rename. One single-column
 * shape per grain so each `DailyRollup{Grain}` view keeps only its own ref.
 */
export const ASSET_RELATION: Shape = [col('assetEntity', 'unitless', 'Reference to the asset model entity.')];

/** The company-grain relation carried by `DailyRollupCompany_Agent`. */
export const COMPANY_RELATION: Shape = [col('companyEntity', 'unitless', 'Reference to the company model entity.')];

/** The field-grain relation carried by `DailyRollupField_Agent`. */
export const FIELD_RELATION: Shape = [col('fieldEntity', 'unitless', 'Reference to the field model entity.')];

/** The location-grain relation carried by `DailyRollupLocation_Agent`. */
export const LOCATION_RELATION: Shape = [
  col('locationEntity', 'unitless', 'Reference to the location model entity (facility or network node).'),
];

/** The reservoir-grain relation carried by `DailyRollupReservoir_Agent`. */
export const RESERVOIR_RELATION: Shape = [
  col('reservoirEntity', 'unitless', 'Reference to the reservoir model entity.'),
];

/**
 * The well-grain relation carried by `DailyRollupOutput_Well`. The source
 * names it `entityName` (a relation to the well entity despite the name), so it
 * is kept verbatim as the grain ref.
 */
export const WELL_RELATION: Shape = [col('entityName', 'unitless', 'Reference to the well model entity.')];

/**
 * The daily-rollup measure family shared by every `DailyRollup{Grain}_Agent`
 * source and `DailyRollupOutput_Well`: allocated production and injection
 * volumes + rates plus unconstrained potential and planning target rates for
 * oil/gas/water. The five Agent grain schemas (Asset/Company/Field/Location/
 * Reservoir) and the well-output schema carry this IDENTICAL 20-timeseries set,
 * so ONE shape fronts every grain — only the kept grain relation differs.
 *
 * The source already exposes the volume/rate pair as two FLAT columns
 * (`dayAllocProdAllocatedOil` + `dayAllocProdAllocatedOilRate`), so the
 * nested VolumeAndRate/AllocatedProduction is authored here as flat canonicals —
 * there is nothing to collapse. Bare canonical source columns:
 * `suffix === canonical`, `prefix:''`, so the reshape emits only a `(columns …)`
 * projection and no `(rename …)`. NOTE the load-bearing source spelling
 * `targetOilproduction` (lowercase `p`) — authoring `targetOilProduction` would
 * silently degrade to `unknownCol`.
 */
